import { createApp } from "vue";
import { createPinia } from "pinia";
import "./assets/styles/style.scss";
import router from "./router";
import App from "./App.vue";
import VueTheMask from "vue-the-mask";

import { i18n } from "./i18n";

import * as Sentry from "@sentry/vue";
import { appEnv, sentryDsn, sentryEnabled, sentryTracesSampleRate } from "@/config/env";
import { productKind } from "@/config/productProfile";
import { captureException } from "@/utils/sentry";
import { initMetrica } from "@/api/metrica";

document.documentElement.dataset.product = productKind;

function registerServiceWorker() {
    if (import.meta.env.PROD && "serviceWorker" in navigator) {
        window.addEventListener("load", () => {
            navigator.serviceWorker.register("/sw.js").catch((error) => {
                console.warn("Service Worker registration failed:", error);
            });
        });
    }
}

const clickOutsideDirective = {
    mounted(el: HTMLElement & { clickOutsideHandler?: (event: Event) => void }, binding: { value: (event: Event) => void }) {
        let initial = true;
        el.clickOutsideHandler = event => {
            if (initial) {
                initial = false;
                return;
            }
            if (!el.contains(event.target as Node)) {
                binding.value(event);
            }
        };
        document.addEventListener("click", el.clickOutsideHandler);
    },
    unmounted(el: HTMLElement & { clickOutsideHandler?: (event: Event) => void }) {
        if (el.clickOutsideHandler) {
            document.removeEventListener("click", el.clickOutsideHandler);
        }
    }
};

const app = createApp(App);

if (sentryEnabled) {
    Sentry.init({
        app,
        dsn: sentryDsn,

        integrations: [
            Sentry.browserTracingIntegration({ router }),
        ],

        environment: appEnv,
        tracesSampleRate: sentryTracesSampleRate,
    });

    app.config.errorHandler = (err, instance, info) => {
        captureException(err, {
            tags: { source: 'vue:errorHandler', info },
            extra: { component: instance?.$?.type?.name || instance?.$options?.name || 'unknown' },
        });
    };

    window.addEventListener('unhandledrejection', (event) => {
        captureException(event.reason, {
            tags: { source: 'unhandledrejection' },
        });
    });
}

const pinia = createPinia();

app
    .use(pinia)
    .use(router)
    .use(VueTheMask)
    .use(i18n)
    .directive("click-outside", clickOutsideDirective)
    .mount("#app");

initMetrica();
registerServiceWorker();